// The command→audible latency probe (P0.6, distribution added at P2.10, moved here at P2.12).
//
// Neutral ground, for the reason every file in /platform/types is: the audio thread writes it and
// the main thread reads it, and neither may import the other. `/audio/worklet` is a separate
// TypeScript project with different globals, and `/audio/engine` pulling
// `class X extends AudioWorkletProcessor` into the main-thread bundle would throw on evaluation.
// So the two sides share a vocabulary neither owns.
//
// **This lived in `worklet.ts` until P2.12**, alongside the tone generator's protocol. When the
// tone generator was deleted that file would have been left holding nothing but the probe, under a
// name that described its former contents — so the probe moved to a file named after what it is.
// The layout, the offsets and the reasoning are unchanged; only the address is new.

/**
 * The command→apply latency probe (Architecture §4.5: "an internal latency probe (loopback
 * estimate of command→output time) feeds telemetry so NFR-P1 is measured in the field, not
 * assumed").
 *
 * Its own small buffer rather than postMessage, because the worklet writes it and postMessage
 * allocates — an allocation on the audio thread is a future GC pause, and a GC pause is the
 * click NFR-P2 budgets. Int32 rather than float64 for the same reason Atomics exist: a 32-bit
 * store is atomic and a 64-bit float write is not. Microseconds keep three decimal places of a
 * millisecond inside an int32 for ±35 minutes, which is longer than any latency worth recording.
 */
export const COMMAND_PROBE = {
  /** i32 — microseconds between a command's timestamp and the block that applied it. */
  LAST_APPLY_MICROS: 0,
  /** i32 — the worst seen since reset. A mean hides the glitch; the max is the whole tail. */
  MAX_APPLY_MICROS: 4,
  /** i32 — commands applied since reset. */
  APPLIED_COUNT: 8,
  /**
   * i32[LATENCY_HISTOGRAM.BUCKETS] — command→applied, bucketed (P2.10).
   *
   * The three scalars above cannot answer NFR-P1 and P0.12 said so at the time: the requirement is
   * "≤35 ms **p90**", the probe reported a max, and the bench duly checked the max and called
   * itself "stricter than the budget". It is not stricter, it is a different question — one
   * outlier fails a machine a p90 would pass, and a budget that fails healthy hardware gets
   * ignored inside a week. This is the distribution that makes the real check possible.
   *
   * Its own cache line, away from the scalars: those are written on every command and read by a
   * 250 ms poll, and a histogram sharing their line would drag the whole thing between cores on
   * every add. Same reasoning, same offset, as RENDER_STATS.INTERVAL_HISTOGRAM.
   */
  APPLY_HISTOGRAM: 64,
} as const;

/**
 * How the probe buckets command→applied time (P2.10).
 *
 * Deliberately its own constant rather than an import of FRAME_HISTOGRAM, even though the numbers
 * currently match. A frame budget and a latency budget are different claims about different
 * hardware, and sharing a constant would mean tuning one silently retunes the other.
 *
 * **250 µs buckets.** NFR-P1's budget is 35 ms, so a bucket is 0.7% of it — far finer than any
 * difference anyone acts on, and finer than the ±1 block (2.7 ms at 48 kHz) of quantisation the
 * apply-at-the-next-block-boundary design builds in anyway.
 *
 * **256 buckets, so 63.75 ms of range.** What lands here is the *engine's* share — command written
 * to command applied — which P0.6 measured at ~8 ms and P0.12 at similar. The device's output
 * latency is added afterwards on the main thread (`readDistribution`'s `offsetMs`), because it is
 * a constant for the life of the context and does not belong in an audio-thread add. So the range
 * needs to cover a badly struggling engine, not a badly struggling engine plus a 40 ms output
 * path. Anything worse than 63.75 ms lands in the overflow bucket and is reported as such rather
 * than being lost or silently clamped.
 */
export const LATENCY_HISTOGRAM = {
  BUCKETS: 256,
  /** Bucket i covers [i·250 µs, (i+1)·250 µs). */
  BUCKET_MICROS: 250,
  /** The last bucket is unbounded: everything ≥63.75 ms lands there rather than being lost. */
  OVERFLOW_BUCKET: 255,
} as const;

/**
 * Three scalars on their own cache line, then the histogram: 64 + 1024.
 *
 * Only the audio thread writes it. It was 64 bytes from P0.6 to P2.10, when the histogram landed.
 */
export const COMMAND_PROBE_BYTES = 64 + LATENCY_HISTOGRAM.BUCKETS * 4;
